/**
 * カレンダーペイン (全画面表示)
 *
 * - Google Calendar の予定 (read-only) を表示
 * - スケジュール済み Todo をドラッグで移動 (PATCH /todos/{id}/schedule)
 * - 「AI で自動配置」で POST /todos/auto-schedule
 */

import './calendar-styles.css'

import type { EventContentArg } from '@fullcalendar/core'
import type { DropArg, EventDragStartArg } from '@fullcalendar/interaction'
import interactionPlugin from '@fullcalendar/interaction'
import FullCalendar from '@fullcalendar/react'
import timeGridPlugin from '@fullcalendar/timegrid'
import { format } from 'date-fns'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'

import { autoSchedule } from '@/api/services/todo'
import type { TodoResponse } from '@/api/types/todo'
import { useSubmitLock } from '@/hooks/useSubmitLock'
import { ensureUtc } from '@/lib/date-utils'
import { useAppStore } from '@/store/useAppStore'
import { useTodoStore } from '@/store/useTodoStore'

import { AutoScheduleDialog } from './AutoScheduleDialog'
import { CalendarEventContent } from './CalendarEventContent'
import { CalendarToolbar, type CalendarViewMode } from './CalendarToolbar'
import { DayTimelinePane } from './DayTimelinePane'
import { isPointerInside } from './parking-tray-utils'
import { ParkingTray } from './ParkingTray'
import { rescheduleTodo } from './schedule-actions'
import { buildScheduleItems, findItemTodo, isDeadlineTodo, SCHEDULE_COLORS } from './schedule-items'
import { scrollToNow } from './scroll-to-now'
import { SlotCreateDialog } from './SlotCreateDialog'
import { useParkedTodos } from './useParkedTodos'
import { type IsoRange, useScheduleSources } from './useScheduleSources'
import { useSlotCreate } from './useSlotCreate'

const toDateStr = (d: Date) => format(d, 'yyyy-MM-dd')

type CalendarPaneProps = {
  searchParams: URLSearchParams
  setSearchParams: (fn: (prev: URLSearchParams) => URLSearchParams) => void
  onTodoClick: (todo: TodoResponse) => void
}

export const CalendarPane = ({ searchParams, setSearchParams, onTodoClick }: CalendarPaneProps) => {
  const todos = useTodoStore((s) => s.todos)
  const loadTodos = useTodoStore((s) => s.loadTodos)
  const { calendarEvents, workoutSessions, sleepWindows, load: loadSources } = useScheduleSources()
  const hasFullAccess = useAppStore((s) => s.hasFullAccess)
  const parked = useParkedTodos()
  const { openSlot, dialogProps: slotDialogProps } = useSlotCreate()
  const trayRef = useRef<HTMLDivElement>(null)
  const droppedOnTrayRef = useRef(false)
  const calendarWrapRef = useRef<HTMLDivElement>(null)
  const toolbarWrapRef = useRef<HTMLDivElement>(null)
  const pointerUpCleanupRef = useRef<(() => void) | null>(null)

  // viewMode をクエリパラメータ → localStorage の優先順で読み込み
  const viewParam = searchParams.get('view')
  const VALID_VIEWS: readonly CalendarViewMode[] = ['week', 'day']
  type ViewMode = CalendarViewMode
  const storedView = localStorage.getItem('todo-calendar-view') as ViewMode | null
  const viewMode: ViewMode = VALID_VIEWS.includes(viewParam as ViewMode)
    ? (viewParam as ViewMode)
    : storedView && VALID_VIEWS.includes(storedView)
      ? storedView
      : 'week'
  const setViewMode = useCallback(
    (v: ViewMode) => {
      localStorage.setItem('todo-calendar-view', v)
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        next.set('view', v)
        return next
      })
    },
    [setSearchParams],
  )

  // 日付をクエリパラメータから読み込み
  const dateParam = searchParams.get('date')
  const initialDate = useMemo(() => {
    if (dateParam) {
      const d = new Date(dateParam)
      if (!isNaN(d.getTime())) return d
    }
    return new Date()
  }, [dateParam])

  const calendarRef = useRef<InstanceType<typeof FullCalendar>>(null)
  const [calendarTitle, setCalendarTitle] = useState('')
  const { isSubmitting: isScheduling, run: runSchedule } = useSubmitLock()
  // 現在カレンダーが表示している範囲（datesSet で更新）
  const lastRangeRef = useRef<IsoRange | null>(null)

  const fcEvents = useMemo(() => {
    const items = buildScheduleItems({
      todos,
      calendarEvents,
      workoutSessions,
      sleepWindows,
      hiddenTodoIds: parked.parkedIds,
    }).map((item) => ({
      id: item.id,
      title: [item.icon, item.title, item.badge].filter(Boolean).join(' '),
      start: item.start,
      end: item.end,
      allDay: item.allDay,
      editable: item.editable,
      color: SCHEDULE_COLORS[item.kind],
      extendedProps: { todoId: item.todoId },
    }))
    // 締切は終日枠に出す
    const deadlines = todos.filter(isDeadlineTodo).map((t) => ({
      id: `deadline-${t.id}`,
      title: `🔔 ${t.title}`,
      start: toDateStr(new Date(ensureUtc(t.deadline as string))),
      allDay: true,
      editable: false,
      color: SCHEDULE_COLORS.deadline,
      extendedProps: { todoId: t.id },
    }))
    return [...items, ...deadlines]
  }, [todos, calendarEvents, workoutSessions, sleepWindows, parked.parkedIds])

  const handleEventClick = (info: { event: { extendedProps?: Record<string, unknown> } }) => {
    const found = findItemTodo(todos, info.event.extendedProps?.todoId as number | undefined)
    if (found) onTodoClick(found)
  }

  const handleSelect = (info: { start: Date; end: Date }) => openSlot(info.start, info.end)

  const handleDateClick = (info: { date: Date }) => openSlot(info.date, new Date(info.date.getTime() + 30 * 60 * 1000))

  const handleEventChange = async (info: {
    event: { extendedProps?: Record<string, unknown>; start: Date | null; end: Date | null }
    revert: () => void
  }) => {
    if (droppedOnTrayRef.current) {
      droppedOnTrayRef.current = false
      info.revert()
      return
    }
    const todoId = info.event.extendedProps?.todoId as number | undefined
    if (!todoId || !info.event.start || !info.event.end) {
      info.revert()
      return
    }
    if (!(await rescheduleTodo(todoId, info.event.start, info.event.end))) info.revert()
  }

  // ドラッグ中に state を更新すると FullCalendar が再描画されドラッグが切れるため DOM 属性で切り替える
  const handleEventDragStart = (info: EventDragStartArg) => {
    droppedOnTrayRef.current = false
    trayRef.current?.setAttribute('data-dragging', 'true')
    const todoId = info.event.extendedProps?.todoId as number | undefined
    if (!todoId) return
    // eventDragStop は戻りアニメーション完了後に来るため、指を離した時点で保留を確定する
    const onPointerUp = (ev: MouseEvent | TouchEvent) => {
      pointerUpCleanupRef.current?.()
      if (!isPointerInside(trayRef.current, ev)) return
      droppedOnTrayRef.current = true
      calendarWrapRef.current?.setAttribute('data-parking', 'true')
      parked.park(todoId)
    }
    document.addEventListener('mouseup', onPointerUp, true)
    document.addEventListener('touchend', onPointerUp, true)
    pointerUpCleanupRef.current = () => {
      document.removeEventListener('mouseup', onPointerUp, true)
      document.removeEventListener('touchend', onPointerUp, true)
      pointerUpCleanupRef.current = null
    }
  }

  const handleEventDragStop = () => {
    pointerUpCleanupRef.current?.()
    trayRef.current?.removeAttribute('data-dragging')
    calendarWrapRef.current?.removeAttribute('data-parking')
  }

  const handleParkedDrop = async (info: DropArg) => {
    if (isPointerInside(trayRef.current, info.jsEvent)) return
    if (info.allDay) {
      toast.error('時間枠にドロップしてください')
      return
    }
    await parked.place(Number(info.draggedEl.dataset.parkedId), info.date)
  }

  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false)
  // AI 配置は管理者本人のカレンダー・体調を使うので管理者のみ
  const openAutoSchedule = hasFullAccess ? () => setIsScheduleDialogOpen(true) : undefined

  const runAutoSchedule = async (params: {
    mode: 'unscheduled' | 'reschedule_all' | 'reschedule_range'
    rangeStart?: string
    rangeEnd?: string
    customInstructions?: string
  }) => {
    setIsScheduleDialogOpen(false)
    await runSchedule(async () => {
      try {
        const req: Parameters<typeof autoSchedule>[0] = {
          horizon_days: 7,
          weekday_work: true,
          include_scheduled: params.mode === 'reschedule_all',
          custom_instructions: params.customInstructions,
        }
        if (params.mode === 'reschedule_range') {
          req.range_start = params.rangeStart
          req.range_end = params.rangeEnd
        }
        const res = await autoSchedule(req)
        await loadTodos({ include_done: true })
        if (lastRangeRef.current) await loadSources(lastRangeRef.current)
        toast.success(`${res.assignments.length}件配置 / 未配置 ${res.unscheduled_todo_ids.length}件`)
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'AI スケジュール失敗')
      }
    })
  }

  // FullCalendar の曜日ヘッダーを sticky ツールバーの直下に固定する (state を使うと FullCalendar が再描画される)
  const isDayView = viewMode === 'day'
  useEffect(() => {
    const toolbar = toolbarWrapRef.current
    const wrap = calendarWrapRef.current
    if (isDayView || !toolbar || !wrap) return
    const ro = new ResizeObserver(() => wrap.style.setProperty('--calendar-toolbar-h', `${toolbar.offsetHeight}px`))
    ro.observe(toolbar)
    return () => ro.disconnect()
  }, [isDayView])

  // 週ビューを表示したら現在時刻が見える位置までスクロール (FullCalendar の描画後に行う)
  useEffect(() => {
    if (isDayView) return
    const id = requestAnimationFrame(() => {
      const slots = calendarWrapRef.current?.querySelector('.fc-timegrid-slots')
      if (slots instanceof HTMLElement) scrollToNow(slots)
    })
    return () => cancelAnimationFrame(id)
  }, [isDayView])

  // 初期日付を FullCalendar に反映 (マウント時のみ)
  const initialDateApplied = useRef(false)
  useEffect(() => {
    if (initialDateApplied.current) return
    initialDateApplied.current = true
    if (isDayView) return
    const api = calendarRef.current?.getApi()
    if (!api || !dateParam) return
    api.gotoDate(initialDate)
    setCalendarTitle(api.view.title)
  }, [isDayView, dateParam, initialDate])

  /** カレンダーの表示日付をクエリに同期 */
  const syncDateToQuery = useCallback(() => {
    const api = calendarRef.current?.getApi()
    if (!api) return
    setCalendarTitle(api.view.title)
    const dateStr = toDateStr(api.getDate())
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('date', dateStr)
      return next
    })
  }, [setSearchParams])

  /** 表示範囲が変わったら同期 + その範囲の Todo（未完了全件 + 範囲内の完了）と予定を取得 */
  const handleDatesSet = useCallback(
    (arg: { start: Date; end: Date }) => {
      syncDateToQuery()
      const range = { start: arg.start.toISOString(), end: arg.end.toISOString() }
      lastRangeRef.current = range
      void loadSources(range)
    },
    [syncDateToQuery, loadSources],
  )

  const handleCalendarPrev = () => {
    const api = calendarRef.current?.getApi()
    if (!api) return
    api.prev()
    syncDateToQuery()
  }

  const handleCalendarNext = () => {
    const api = calendarRef.current?.getApi()
    if (!api) return
    api.next()
    syncDateToQuery()
  }

  const handleCalendarToday = () => {
    const api = calendarRef.current?.getApi()
    if (!api) return
    api.today()
    syncDateToQuery()
  }

  return (
    <>
      <div className="relative flex flex-col flex-1 min-h-0 overflow-clip">
        {isDayView ? (
          <DayTimelinePane
            onViewChange={(v) => setViewMode(v)}
            initialDate={initialDate}
            onDateChange={(d) => {
              const dateStr = toDateStr(d)
              setSearchParams((prev) => {
                const next = new URLSearchParams(prev)
                next.set('date', dateStr)
                return next
              })
            }}
            onAutoSchedule={openAutoSchedule}
            isScheduling={isScheduling}
            onTodoClick={onTodoClick}
            parked={parked}
          />
        ) : (
          <>
            <div ref={toolbarWrapRef} className="sticky top-0 z-40 bg-background">
              <CalendarToolbar
                title={calendarTitle}
                activeView={viewMode}
                onPrev={handleCalendarPrev}
                onNext={handleCalendarNext}
                onToday={handleCalendarToday}
                onViewChange={setViewMode}
                onAutoSchedule={openAutoSchedule}
                isScheduling={isScheduling}
              />
            </div>
            <div ref={calendarWrapRef} className="todo-calendar px-2 pb-2 bg-background">
              <FullCalendar
                ref={calendarRef}
                plugins={[timeGridPlugin, interactionPlugin]}
                initialView="timeGridWeek"
                height="auto"
                events={fcEvents}
                editable
                eventDrop={handleEventChange}
                eventResize={handleEventChange}
                eventDragStart={handleEventDragStart}
                eventDragStop={handleEventDragStop}
                droppable
                drop={handleParkedDrop}
                eventClick={handleEventClick}
                eventContent={(arg: EventContentArg) => <CalendarEventContent arg={arg} />}
                selectable
                select={handleSelect}
                dateClick={handleDateClick}
                slotMinTime="00:00:00"
                slotMaxTime="24:00:00"
                slotDuration="00:30:00"
                snapDuration="00:15:00"
                slotLabelInterval="01:00:00"
                slotLabelContent={(arg: { date: Date }) => {
                  const h = arg.date.getHours().toString().padStart(2, '0')
                  return `${h}`
                }}
                nowIndicator
                allDaySlot
                allDayContent={() => <span className="text-[0.65rem] text-muted-foreground">締切</span>}
                locale="ja"
                dayHeaderContent={(arg: { date: Date }) => {
                  const dow = arg.date.toLocaleDateString('ja', { weekday: 'short' })
                  const day = arg.date.getDate()
                  const isToday = arg.date.toDateString() === new Date().toDateString()
                  return (
                    <div className="flex flex-col items-center py-1">
                      <span
                        className={`text-[0.65rem] leading-tight ${isToday ? 'text-primary' : 'text-muted-foreground'}`}
                      >
                        {dow}
                      </span>
                      <span
                        className={`text-lg font-normal leading-tight mt-0.5 ${
                          isToday
                            ? 'bg-primary text-primary-foreground rounded-full w-8 h-8 flex items-center justify-center'
                            : 'text-foreground'
                        }`}
                      >
                        {day}
                      </span>
                    </div>
                  )
                }}
                headerToolbar={false}
                datesSet={handleDatesSet}
                views={{
                  timeGridWeek: { titleFormat: { year: 'numeric', month: 'long' } },
                }}
                buttonText={{ today: '今日', week: '週' }}
              />
            </div>
            <ParkingTray
              trayRef={trayRef}
              todos={parked.parkedTodos}
              onRemove={parked.unpark}
              dragMode="fullcalendar"
            />
          </>
        )}
      </div>
      <AutoScheduleDialog
        open={isScheduleDialogOpen}
        onClose={() => setIsScheduleDialogOpen(false)}
        onSubmit={runAutoSchedule}
        isScheduling={isScheduling}
      />
      {slotDialogProps && <SlotCreateDialog {...slotDialogProps} />}
    </>
  )
}
